import type { PartnerCockpitNotification } from '@houzy/schemas/partner-cockpit/notifications';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Link } from '@tanstack/react-router';
import { useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { NotificationBell } from '../../../components/layout/NotificationBell';
import { useTrackEvent } from '../../../lib/trackingEvents';
import {
    useMarkAllNotificationsAsRead,
    useMarkNotificationAsRead,
    useRecentNotifications,
    useUnreadNotificationsCount,
} from '../api/notificationsApi';
import { useAutoReadInformationalNotifications } from '../state/useAutoReadInformationalNotifications';
import { NotificationItem } from './NotificationItem';

const MENU_WIDTH = 394;
const LIST_MAX_HEIGHT = 360;

export function NotificationMenu(): ReactElement {
    const { t } = useTranslation();
    const anchorRef = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);
    const track = useTrackEvent();

    const { data: unreadCount } = useUnreadNotificationsCount();
    const { data: notifications, isError } = useRecentNotifications(open);
    const markAsRead = useMarkNotificationAsRead();
    const markAllAsRead = useMarkAllNotificationsAsRead();

    const hasUnread = (unreadCount ?? 0) > 0;
    const recentNotifications = notifications ?? [];

    const [closedWith, setClosedWith] = useState<PartnerCockpitNotification[] | undefined>(undefined);
    useAutoReadInformationalNotifications(closedWith);

    const close = (): void => {
        setClosedWith(recentNotifications);
        setOpen(false);
    };

    const handleSelect = (notification: PartnerCockpitNotification): void => {
        track('Notification Clicked', { notificationType: notification.type });
        markAsRead.mutate(notification.uuid);
        close();
    };

    return (
        <>
            <Box ref={anchorRef} sx={{ display: 'inline-flex' }}>
                <NotificationBell
                    hasNotifications={hasUnread}
                    onClick={() => {
                        track('Notifications Opened', {});
                        setOpen(true);
                    }}
                />
            </Box>

            <Popover
                open={open}
                anchorEl={anchorRef.current}
                onClose={close}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                // The fixed width overflows a phone; MUI keeps the popover 16px clear of each edge.
                slotProps={{ paper: { sx: { width: { xs: 'calc(100vw - 32px)', sm: MENU_WIDTH }, mt: 1 } } }}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ px: 2, py: 1.5, gap: 1 }}>
                    <Typography variant="subtitle1" component="h2">
                        {t('NOTIFICATIONS.TITLE')}
                    </Typography>
                    <Button
                        size="small"
                        disabled={!hasUnread || markAllAsRead.isPending}
                        onClick={() => markAllAsRead.mutate()}>
                        {t('NOTIFICATIONS.MARK_ALL_READ')}
                    </Button>
                </Stack>
                <Divider />

                {isError ? (
                    <Alert severity="error" sx={{ m: 2 }}>
                        {t('ERRORS.GENERIC.TITLE')}
                    </Alert>
                ) : recentNotifications.length > 0 ? (
                    <List disablePadding sx={{ maxHeight: LIST_MAX_HEIGHT, overflowY: 'auto' }}>
                        {recentNotifications.map((notification) => (
                            <NotificationItem
                                key={notification.uuid}
                                notification={notification}
                                onSelect={handleSelect}
                            />
                        ))}
                    </List>
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ px: 2.5, py: 3, textAlign: 'center' }}>
                        {t('NOTIFICATIONS.EMPTY_UNREAD')}
                    </Typography>
                )}

                <Divider />
                <Stack alignItems="center" sx={{ p: 1 }}>
                    <Button component={Link} to="/notifications" onClick={close} endIcon={<ArrowForwardIcon />}>
                        {t('NOTIFICATIONS.VIEW_ALL')}
                    </Button>
                </Stack>
            </Popover>
        </>
    );
}
